import { CustomPiece, KewloxPieceType } from '../pieces/CustomPiece';
import { SearchInfo } from '../Data';
import { PositionPiece } from '../type';
import { findColorById, getBaseColor } from '../utils/colorHelper';
import { ComponentKewlox } from '../pieces/ComponentKewlox';
import { PlateType } from '../constants';

export class PiecesComponentsList<T extends KewloxPieceType> {
  private _list: Map<number, T>;
  private _pickingList: Map<string, number>;

  constructor(arrayComponent: T[]) {
    this._list = new Map<number, T>();
    this._pickingList = new Map<string, number>();

    // Sort the list
    arrayComponent.sort((a, b) => a.compareTo(b));

    for (const element of arrayComponent) {
      this._list.set(element.id, element);
      this._pickingList.set(element.getKey(), element.id);
    }
  }

  get list(): Map<number, T> {
    return this._list;
  }

  set list(value: Map<number, T>) {
    this._list = value;
  }

  get pickingList(): Map<string, number> {
    return this._pickingList;
  }

  get(id: number): T {
    const element = this._list.get(id);
    if (!element)
      throw new Error(`The pieces with id ${JSON.stringify(id)} don't exist`);
    return element;
  }

  getFromKey(key: string) {
    const elementIndex = this._pickingList.get(key);
    if (elementIndex) return this._list.get(elementIndex);
  }

  getFromProps(keyObj: object) {
    const key = JSON.stringify(keyObj);
    const elementIndex = this._pickingList.get(key);
    if (elementIndex) return this._list.get(elementIndex);
  }

  getFromReference(reference: string) {
    let value: T | undefined = undefined;
    for (const [key, item] of this._list.entries()) {
      if (item.reference === reference) {
        value = item;
        break;
      }
    }
    return value;
  }

  findCloserObj(searchInfo: SearchInfo): any {
    let notMatch = false;
    let closer = undefined;
    for (const [key, item] of this._list.entries()) {
      notMatch = false;
      // Filter the list
      for (const itemProp of Object.getOwnPropertyNames(item)) {
        if (notMatch) break;
        for (const searchProp in searchInfo.key) {
          if (
            itemProp === searchProp &&
            !searchInfo.lookup.includes(itemProp)
          ) {
            // @ts-ignore
            let itemVal = item[itemProp];
            if (itemProp === 'color') itemVal = itemVal.id;
            // @ts-ignore
            if (itemVal !== searchInfo.key[itemProp]) {
              notMatch = true;
              break;
            }
          }
        }
        if (!notMatch && searchInfo.include) {
          if (!(searchInfo.obj as ComponentKewlox).includeInto(item)) {
            notMatch = true;
          }
        }
      }
      if (!notMatch) {
        closer = item;

        if (item.compareTo(searchInfo.obj as ComponentKewlox) === 0) {
          return { closerPiece: item, equal: true };
        }
        if (item.compareTo(searchInfo.obj as ComponentKewlox) >= 0) {
          return { closerPiece: item, equal: false };
        }
      }
    }

    if (closer) {
      return { closerPiece: closer, equal: false };
    }

    return null;
  }

  findCloser(searchInfo: SearchInfo) {
    // Loop values
    let notMatch = false;
    let closeValue: any = 0;
    for (const [key, item] of this._list) {
      notMatch = false;
      // Filter the list
      for (const itemProp of Object.getOwnPropertyNames(item)) {
        if (notMatch) break;
        for (const searchProp in searchInfo.key) {
          if (
            itemProp === searchProp &&
            !searchInfo.lookup.includes(itemProp)
          ) {
            // @ts-ignore
            let itemVal = item[itemProp];
            if (itemProp === 'color') itemVal = itemVal.id;
            // @ts-ignore
            if (itemVal !== searchInfo.key[itemProp]) {
              notMatch = true;
              break;
            }
          }
        }
      }
      if (!notMatch) {
        // Check if the lookup is OK with the value
        for (const lookupField of searchInfo.lookup) {
          // @ts-ignore
          if (item[lookupField] < searchInfo.key[lookupField]) {
            notMatch = true;
            break;
          }
          notMatch = false;
        }
        if (notMatch) closeValue = item;
        else return item;
      }
    }
    if (closeValue !== 0) return closeValue as T;
  }

  getCustom(
    searchInfos: SearchInfo,
    position: PositionPiece,
    isDrawer = false,
    plateType?: PlateType,
  ): CustomPiece<T> {
    let color = undefined;

    let base = this.getFromProps(searchInfos.key);
    if (base && !plateType) {
      if (searchInfos.key.hasOwnProperty('color')) {
        // @ts-ignore
        color = base.color;
      }
      return new CustomPiece<T>(
        base,
        false,
        position,
        undefined,
        undefined,
        undefined,
        undefined,
        color,
        undefined,
        plateType,
      );
    } else {
      // Check if associate color exist
      if (searchInfos.key.hasOwnProperty('color')) {
        const colorId = searchInfos.key.color;
        color = findColorById(colorId as number);

        const baseColor = getBaseColor(
          findColorById(colorId as number),
          isDrawer,
        );

        searchInfos.key.color = baseColor.id;
        base = this.getFromProps(searchInfos.key);
        if (base && !plateType) {
          // @ts-ignore
          return new CustomPiece<T>(
            base,
            false,
            position,
            undefined,
            undefined,
            undefined,
            undefined,
            color,
            undefined,
            plateType,
          );
        }
      }

      // It's a custom piece
      let closerPiece = undefined;
      let equal = false;
      if (searchInfos.obj) {
        const result = this.findCloserObj(searchInfos);
        if (result) {
          closerPiece = result.closerPiece;
          equal = result.equal;
        }
      } else {
        closerPiece = this.findCloser(searchInfos);
      }

      if (!closerPiece) {
        // eslint-disable-next-line no-console
        console.error(
          'Could not find any piece as base for custom piece 22',
          searchInfos,
        );
        // TODO: add extra infos searchInfos for Sentry
        throw new Error(
          `Could not find any piece as base for custom piece ${JSON.stringify(
            searchInfos.key,
          )}`,
        );
      }

      let width = undefined;
      if (searchInfos.lookup.includes('width')) {
        width = searchInfos.key.width;
      }

      let depth = undefined;
      if (searchInfos.lookup.includes('depth')) {
        depth = searchInfos.key.depth;
      }

      let custom = true;

      if (plateType && equal) {
        custom = false;
      }

      return new CustomPiece<T>(
        closerPiece,
        custom,
        position,
        searchInfos.key.height,
        width,
        depth,
        undefined,
        color,
        undefined,
        plateType,
      );
    }
  }

  get properties() {
    return [];
  }
}
